import { useEffect, useState } from "react";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Button } from "@/components/base/buttons/button";
import { SlideOver } from "@/components/application/slideout-menus/slide-over";
import {
  formatKc,
  type AssignCardHolderPayload,
  type CardCandidate,
  type UnassignedCardGroup,
} from "../../schemas/budget";

/**
 * UC-002 — přiřazení držitele kartě (per spec-proposal 2026-05-29-02).
 *
 * Jednotka atribuce = tankovací karta. Admin přiřadí každé kartě držitele
 * (osobu), které se náklad refakturuje. „Neznámé karty" (transakce na kartu,
 * která není v inventáři) se zde NEpřiřazují — vyžadují nový sync; zobrazují se
 * jen jako varování.
 *
 * Submit → assignCardHolder mutation (fuel_card_id → holder_id); po úspěchu se
 * panel zavře a rozpočet přepočítá.
 */

export interface HolderOption {
  holder_id: string;
  display_name: string;
}

export interface UnassignedSpzSlideOverProps {
  open: boolean;
  budgetId: string | null;
  /** Karty zóny rozpočtu (pro přiřazení držitele). */
  candidates: CardCandidate[];
  /** Transakce na neznámou kartu (nelze přiřadit — jen varování). */
  unknownGroups: UnassignedCardGroup[];
  /** Možní držitelé (PO/FO registr tenantu). */
  holders: HolderOption[];
  loading?: boolean;
  submitting?: boolean;
  submitError?: string | null;
  onClose: () => void;
  onSubmit: (payload: AssignCardHolderPayload) => void;
}

export function UnassignedSpzSlideOver({
  open,
  budgetId,
  candidates,
  unknownGroups,
  holders,
  loading,
  submitting,
  submitError,
  onClose,
  onSubmit,
}: UnassignedSpzSlideOverProps) {
  // fuel_card_id → holder_id (jen změny oproti aktuálnímu stavu).
  const [assignments, setAssignments] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) setAssignments({});
  }, [open, budgetId]);

  function selectHolder(cardId: string, value: string): void {
    setAssignments((prev) => {
      const next = { ...prev };
      if (value === "") delete next[cardId];
      else next[cardId] = value;
      return next;
    });
  }

  function handleSubmit(): void {
    if (Object.keys(assignments).length === 0) return;
    onSubmit({ holder_assignments: { ...assignments } });
  }

  const hasAssignments = Object.keys(assignments).length > 0;

  return (
    <SlideOver
      open={open}
      title="Přiřazení držitelů karet"
      onClose={onClose}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={submitting}>
            Zrušit
          </Button>
          <Button
            color="primary"
            onPress={handleSubmit}
            isDisabled={!hasAssignments || submitting}
            data-testid="unassigned-spz-submit"
          >
            {submitting ? "Ukládám…" : "Přiřadit a přepočítat"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4" data-testid="unassigned-spz-panel">
        {loading ? (
          <p>Načítám karty…</p>
        ) : (
          <>
            {unknownGroups.length > 0 && (
              <InlineAlert
                variant="warning"
                title={`Transakce na neznámou kartu: ${unknownGroups.length}`}
              >
                Tyto karty nejsou v inventáři — spusťte sync, aby se objevily.
                Do té doby nelze rozpočet schválit.
                <ul className="list-none m-0 p-0 flex flex-col gap-4">
                  {unknownGroups.map((group) => (
                    <li
                      key={group.card_number_display ?? "no-card"}
                      data-testid={`unknown-card-${group.card_number_display ?? "no-card"}`}
                    >
                      <strong>{group.card_number_display ?? "(bez čísla karty)"}</strong>
                      {" · "}
                      {group.transaction_count} transakcí ·{" "}
                      {formatKc(group.total_amount_kc)}
                    </li>
                  ))}
                </ul>
              </InlineAlert>
            )}

            {candidates.length === 0 ? (
              <InlineAlert variant="info" title="Žádné karty">
                Pro tuto zónu nejsou žádné karty.
              </InlineAlert>
            ) : (
              <ul className="list-none m-0 p-0 flex flex-col gap-4">
                {candidates.map((card) => (
                  <li
                    key={card.fuel_card_id}
                    className="flex flex-col gap-2 rounded-lg border border-secondary p-4"
                    data-testid={`card-holder-item-${card.fuel_card_id}`}
                  >
                    <div className="flex items-center justify-between gap-2 text-md">
                      <strong>{card.card_label ?? "—"}</strong>
                      <span className="text-sm text-tertiary">
                        {card.card_number_display ?? ""}
                        {card.holder_label
                          ? ` · držitel: ${card.holder_label}`
                          : " · bez držitele"}
                      </span>
                    </div>
                    <label className="flex flex-col gap-1 text-sm">
                      <span>Držitel:</span>
                      <select
                        className="block w-full min-h-touch rounded-lg bg-primary px-3 py-2 text-md text-primary ring-1 ring-inset ring-primary outline-focus-ring focus:ring-2 focus:ring-brand disabled:opacity-50"
                        value={assignments[card.fuel_card_id] ?? card.holder_id ?? ""}
                        onChange={(e) =>
                          selectHolder(card.fuel_card_id, e.target.value)
                        }
                        data-testid={`card-holder-select-${card.fuel_card_id}`}
                      >
                        <option value="">— vyberte —</option>
                        {holders.map((h) => (
                          <option key={h.holder_id} value={h.holder_id}>
                            {h.display_name}
                          </option>
                        ))}
                      </select>
                    </label>
                  </li>
                ))}
              </ul>
            )}

            {submitError && (
              <InlineAlert variant="error" title="Chyba při ukládání">
                {submitError}
              </InlineAlert>
            )}
          </>
        )}
      </div>
    </SlideOver>
  );
}
